import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const AdminBulkUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx')) {
        setError('Please upload an Excel file (.xlsx)');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/admin/users/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
      setFile(null);
    } catch (err) {
      const message = err.response?.data?.detail || 'Upload failed. Please try again.';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container admin-container" style={{ maxWidth: '800px' }}>
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="page-title">Bulk Upload MRs</h1>
          <p className="page-subtitle">Upload Excel file to add multiple users</p>
        </div>
        <Link to="/admin" className="btn btn-secondary" style={{ width: 'auto', padding: '10px 20px' }}>
          ← Back
        </Link>
      </div>

      {/* Instructions */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>
          Excel File Format
        </h3>
        <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
          Your Excel file should have the following columns:
        </p>
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '13px',
          overflowX: 'auto'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>mr_name</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>mr_emp_code</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>mr_hq</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', color: '#666' }}>John Doe</td>
                <td style={{ padding: '8px', color: '#666' }}>EMP001</td>
                <td style={{ padding: '8px', color: '#666' }}>Mumbai</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', color: '#666' }}>Jane Smith</td>
                <td style={{ padding: '8px', color: '#666' }}>EMP002</td>
                <td style={{ padding: '8px', color: '#666' }}>Delhi</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '12px' }}>
          Note: Username will be generated from mr_emp_code, password will be the same as mr_emp_code (user can change later).
        </p>
      </div>

      {/* Upload area */}
      <div className="card">
        <label className="file-upload">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <div className="file-upload-icon">📁</div>
          {file ? (
            <>
              <div className="file-upload-text">{file.name}</div>
              <div className="file-upload-hint">Click to change file</div>
            </>
          ) : (
            <>
              <div className="file-upload-text">Click to select Excel file</div>
              <div className="file-upload-hint">Supports .xlsx format</div>
            </>
          )}
        </label>

        {error && <p className="error-message" style={{ marginTop: '16px' }}>{error}</p>}

        <button
          onClick={handleUpload}
          className="btn btn-primary"
          disabled={!file || uploading}
          style={{ marginTop: '24px' }}
        >
          {uploading ? (
            <>
              <span className="spinner" style={{ marginRight: '8px', width: '20px', height: '20px' }}></span>
              Uploading...
            </>
          ) : (
            '📤 Upload and Create Users'
          )}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="card" style={{ marginTop: '24px' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px',
            color: result.failed > 0 ? '#FF9800' : '#4CAF50'
          }}>
            Upload Results
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '16px',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ 
              backgroundColor: '#e8f5e9', 
              padding: '16px', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#4CAF50' }}>
                {result.created}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Created</div>
            </div>
            <div style={{ 
              backgroundColor: '#fff3e0', 
              padding: '16px', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#FF9800' }}>
                {result.skipped}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Skipped</div>
            </div>
            <div style={{ 
              backgroundColor: '#ffebee', 
              padding: '16px', 
              borderRadius: '8px' 
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f44336' }}>
                {result.failed}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
            </div>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div style={{ 
              backgroundColor: '#ffebee', 
              padding: '16px', 
              borderRadius: '8px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <p style={{ fontWeight: '600', marginBottom: '8px', color: '#c62828' }}>Errors:</p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#c62828' }}>
                {result.errors.map((err, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBulkUpload;
